// ============================================
// pages/Home.tsx
//
// 首页 feed. 接 community-list-projects (Week 2 后端已就绪).
//
// Week 2 重做要点 (官网设计系统):
//   - 套 Layout (导航 + 底部)
//   - 顶部 hero: kicker + grad-ink 大标题 (官网风)
//   - 排序 tabs: 最新 / 热门
//   - 分类筛选 chips
//   - 游标分页 (next_cursor) + "加载更多"
//   - 响应式 grid: 1 / 2 / 3 列
//   - URL ?q= 时做前端关键词过滤兜底 (专门搜索页 Week 2 后续)
// ============================================

import { useEffect, useState, useCallback } from 'react';
import { Puzzle, WifiOff } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import ProjectCard from '../components/ProjectCard';
import LoadingSpinner from '../components/LoadingSpinner';
import { callApi } from '../services/api';
import { loadCategories, getCategoriesSync, type Category } from '../services/categories';
import type { ProjectPublic, ProjectCategory } from '../types/community';

type SortMode = 'latest' | 'hot';
type CardWithAuthor = ProjectPublic & {
  author?: { short_id: string | null; nickname: string; avatar_url: string | null; level?: number };
};

const SORTS: Array<{ id: SortMode; label: string }> = [
  { id: 'latest', label: '最新' },
  { id: 'hot', label: '热门' },
];

export default function Home() {
  const [params] = useSearchParams();
  const q = (params.get('q') || '').trim().toLowerCase();

  const [list, setList] = useState<CardWithAuthor[]>([]);
  const [sort, setSort] = useState<SortMode>('latest');
  const [category, setCategory] = useState<ProjectCategory | 'all'>('all');

  // 分类: 后端定义. 初值用 service 当前缓存 (默认兜底), 拉取完成后刷新.
  const [categories, setCategories] = useState<Category[]>(getCategoriesSync());
  useEffect(() => {
    loadCategories().then(setCategories);
  }, []);

  const [loading, setLoading] = useState(true);     // 首屏 / 切条件
  const [loadingMore, setLoadingMore] = useState(false);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [errored, setErrored] = useState(false);

  // 拉一页. reset=true 时清空重来 (切排序/分类); false 时追加 (加载更多).
  const fetchPage = useCallback(
    async (reset: boolean, cur: string | null) => {
      if (reset) setLoading(true);
      else setLoadingMore(true);
      setErrored(false);

      const r = await callApi<{ list: CardWithAuthor[]; next_cursor: string | null }>(
        'community-list-projects',
        {
          sort,
          category: category === 'all' ? null : category,
          cursor: cur,
        },
        { noAuth: true },
      );

      if (r.ok && Array.isArray(r.list)) {
        const incoming = r.list as CardWithAuthor[];
        setList((prev) => (reset ? incoming : [...prev, ...incoming]));
        setCursor((r.next_cursor as string | null) ?? null);
        setHasMore(Boolean(r.next_cursor));
      } else {
        if (reset) setList([]);
        setHasMore(false);
        // NOT_IMPLEMENTED / 集合未建 → 后端返空, 不算错; 其余才标错
        if (r.error && !['NOT_IMPLEMENTED', 'UNKNOWN_ACTION'].includes(r.error as string)) {
          setErrored(true);
        }
      }

      if (reset) setLoading(false);
      else setLoadingMore(false);
    },
    [sort, category],
  );

  // 切排序/分类 → 重拉第一页
  useEffect(() => {
    setCursor(null);
    fetchPage(true, null);
  }, [fetchPage]);

  // 前端关键词过滤兜底 (URL ?q=)
  const shown = q
    ? list.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          (p.author?.nickname || '').toLowerCase().includes(q) ||
          (p.tags || []).some((t) => t.toLowerCase().includes(q)),
      )
    : list;

  return (
    <Layout width="wide">
      {/* ───── Hero(浅色暖黄科技风) ───── */}
      <section className="pt-6 pb-6">
        <div
          className="relative overflow-hidden rounded-3xl border border-gold-200/70 px-6 sm:px-12 py-12 sm:py-16"
          style={{
            background:
              'radial-gradient(120% 130% at 90% 8%, rgba(224,160,32,.12), transparent 45%), radial-gradient(100% 120% at 100% 95%, rgba(240,193,75,.10), transparent 50%), linear-gradient(180deg,#ffffff,#fdf9ee)',
            boxShadow: '0 30px 60px -42px rgba(224,160,32,.32)',
          }}
        >
          {/* 暖黄网格底纹(渐隐) */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage:
                'linear-gradient(rgba(224,160,32,.07) 1px, transparent 1px), linear-gradient(90deg, rgba(224,160,32,.07) 1px, transparent 1px)',
              backgroundSize: '44px 44px',
              maskImage: 'radial-gradient(circle at 84% 22%, #000 0%, transparent 66%)',
              WebkitMaskImage: 'radial-gradient(circle at 84% 22%, #000 0%, transparent 66%)',
            }}
          />

          {/* 电路板发光走线 + MCU 芯片(暖黄) */}
          <svg
            className="absolute right-0 top-0 h-full w-[540px] hidden sm:block pointer-events-none"
            viewBox="0 0 540 420" fill="none" preserveAspectRatio="xMaxYMid slice"
          >
            <g stroke="rgba(224,160,32,.32)" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round">
              <path d="M540 90 L420 90 L390 120 L290 120" />
              <path d="M540 230 L460 230 L430 200 L350 200" />
              <path d="M540 330 L410 330 L380 300 L320 300" />
            </g>
            <g fill="#e0a020">
              <circle cx="290" cy="120" r="4" /><circle cx="350" cy="200" r="4" /><circle cx="320" cy="300" r="4" />
            </g>
            <rect x="445" y="160" width="84" height="84" rx="12" stroke="rgba(224,160,32,.45)" strokeWidth="1.5" fill="rgba(224,160,32,.05)" />
            <text x="487" y="207" fill="rgba(166,111,18,.75)" fontSize="13" fontFamily="monospace" textAnchor="middle">MCU</text>
            <text x="470" y="120" fill="rgba(224,160,32,.3)" fontSize="22" fontFamily="monospace" fontWeight="bold">&lt;/&gt;</text>
          </svg>

          {/* 文案 */}
          <div className="relative">
            <span className="inline-flex items-center gap-2 text-[13px] font-semibold text-gold-700 bg-gold-soft rounded-pill px-3.5 py-1.5 border border-gold-line">
              <span className="w-1.5 h-1.5 rounded-full bg-gold-500 animate-pulse" />
              作品广场
            </span>
            <h1 className="font-display text-3xl sm:text-5xl font-extrabold mt-5 leading-[1.12] tracking-tight text-ink-900">
              看看大家用 <span style={{ background: 'linear-gradient(120deg,#c8881a,#e0a020)', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>HiPiiDo</span> 搭出了什么
            </h1>
            <p className="text-ink-500 mt-4 text-[15px] sm:text-[17px] leading-relaxed max-w-xl">
              从智能家居到会跑的小车 —— 创客们把 Studio 里的积木，变成真实的物理硬件作品。在这里分享你的创客巧思。
            </p>
          </div>
        </div>
      </section>

      {/* ───── 筛选条(带底色区隔) ───── */}
      <div className="sticky top-16 z-20 -mx-4 px-4 py-3 bg-white/90 backdrop-blur-md border-y border-ink-300/40 shadow-[0_2px_12px_-8px_rgba(15,23,42,0.15)]">
        <div className="flex items-center gap-3 flex-wrap">
          {/* 排序 */}
          <div className="inline-flex p-0.5 rounded-pill bg-white border border-ink-300/40">
            {SORTS.map((s) => (
              <button
                key={s.id}
                onClick={() => setSort(s.id)}
                className={`px-4 py-1.5 rounded-pill text-sm font-semibold transition-colors ${
                  sort === s.id ? 'bg-gold-500 text-white' : 'text-ink-500 hover:text-ink-900'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>

          <span className="w-px h-5 bg-ink-300/50 hidden sm:block" />

          {/* 分类 (后端定义) */}
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setCategory('all')}
              className={`px-3 py-1.5 rounded-pill text-[13px] font-medium border transition-colors ${
                category === 'all'
                  ? 'bg-gold-soft text-gold-700 border-gold-200'
                  : 'bg-white text-ink-500 border-ink-300/40 hover:border-gold-200 hover:text-gold-600'
              }`}
            >
              全部
            </button>
            {categories.map((c) => (
              <button
                key={c.id}
                onClick={() => setCategory(c.id as ProjectCategory)}
                className={`px-3 py-1.5 rounded-pill text-[13px] font-medium border transition-colors ${
                  category === c.id
                    ? 'bg-gold-soft text-gold-700 border-gold-200'
                    : 'bg-white text-ink-500 border-ink-300/40 hover:border-gold-200 hover:text-gold-600'
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ───── 内容 ───── */}
      <div className="py-8 min-h-[40vh]">
        {q && (
          <p className="text-sm text-ink-500 mb-5">
            关键词「<span className="text-gold-700 font-medium">{q}</span>」的结果 ·{' '}
            {shown.length} 个
          </p>
        )}

        {loading ? (
          <div className="py-20 flex justify-center">
            <LoadingSpinner />
          </div>
        ) : errored ? (
          <EmptyState
            icon={<WifiOff className="w-12 h-12" strokeWidth={1.5} />}
            title="加载失败"
            desc="创新工场数据暂时拉取不到,请稍后刷新重试。"
          />
        ) : shown.length === 0 ? (
          <EmptyState
            icon={<Puzzle className="w-12 h-12" strokeWidth={1.5} />}
            title={q ? '没有匹配的作品' : '还没有作品'}
            desc={q ? '换个关键词试试。' : '回到 HiPiiDo Studio,点「分享」发布第一个作品吧!'}
          />
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {shown.map((p) => (
                <ProjectCard key={p.short_id} project={p} showAuthor />
              ))}
            </div>

            {/* 加载更多 (有关键词过滤时不分页, 走前端过滤当前已加载集) */}
            {!q && hasMore && (
              <div className="flex justify-center mt-10">
                <button
                  className="btn-secondary"
                  disabled={loadingMore}
                  onClick={() => fetchPage(false, cursor)}
                >
                  {loadingMore ? '加载中…' : '加载更多'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}

function EmptyState({ icon, title, desc }: { icon: React.ReactNode; title: string; desc: string }) {
  return (
    <div className="bg-white rounded-2xl border border-ink-300/40 py-16 px-6 text-center shadow-card">
      <div className="flex justify-center mb-4 text-gold-300">{icon}</div>
      <h2 className="text-lg font-bold text-ink-900 mb-2">{title}</h2>
      <p className="text-ink-500 text-sm max-w-sm mx-auto leading-relaxed">{desc}</p>
    </div>
  );
}
